/**
 * Fiche tiers.
 *
 * Réunit ce qu'on cherche quand on ouvre un client : ses coordonnées, ses
 * rôles, ses sites, et l'ensemble de son parc — tous sites confondus. La liste
 * des tiers ne servait à rien tant qu'elle ne s'ouvrait sur rien.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, api, type Asset, type Party, type Site } from '../api/client';
import { ContactsTiers } from '../composants/ContactsTiers';
import { FormulaireSite } from '../composants/FormulaireSite';
import { FormulaireTiers } from '../composants/FormulaireTiers';
import { ROLE_LIBELLE } from '../domaine/roles';
import { STATUT_MACHINE } from '../domaine/machines';

export function PartyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [tiers, setTiers] = useState<Party | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [machines, setMachines] = useState<Asset[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [formulaire, setFormulaire] = useState<'tiers' | 'site' | null>(null);

  const charger = useCallback(async () => {
    if (!id) return;
    try {
      const [t, s, m] = await Promise.all([
        api.party(id),
        api.sites(undefined, id),
        api.assets({ customerId: id, take: 500 }),
      ]);
      setTiers(t);
      setSites(s);
      setMachines([...m.items]);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement impossible.');
    }
  }, [id]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const archiver = async () => {
    if (!tiers) return;
    const motif = window.prompt(
      `Archiver « ${tiers.legalName} » ?\n\n` +
        'Le tiers sort des listes mais reste attaché à son historique. ' +
        'Motif (facultatif) :',
    );
    if (motif === null) return;
    try {
      await api.archiveParty(tiers.id, motif || undefined);
      navigate('/parc');
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Archivage impossible.');
    }
  };

  if (erreur && !tiers) {
    return (
      <>
        <div className="message erreur">{erreur}</div>
        <Link to="/parc">← Retour au parc</Link>
      </>
    );
  }

  if (!tiers) {
    return <div className="vide">Chargement…</div>;
  }

  return (
    <>
      <div className="fil-ariane">
        <Link to="/parc">← Parc</Link>
      </div>

      <div className="entete-page">
        <div>
          <h1>{tiers.legalName}</h1>
          <p>
            {tiers.tradeName && <>{tiers.tradeName} · </>}
            {tiers.roles.length > 0
              ? tiers.roles.map((r) => ROLE_LIBELLE[r]).join(', ')
              : 'aucun rôle'}
          </p>
        </div>
        <div className="actions">
          <button className="secondaire" onClick={() => setFormulaire('tiers')}>
            Corriger
          </button>
          <button className="secondaire danger-discret" onClick={() => void archiver()}>
            Archiver
          </button>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}

      <div className="deux-colonnes">
        <div className="carte">
          <h2>Identité</h2>
          <div className="carte-corps">
            <dl className="proprietes">
              <dt>Raison sociale</dt>
              <dd>{tiers.legalName}</dd>
              <dt>Nom commercial</dt>
              <dd>{tiers.tradeName ?? <span className="aide">—</span>}</dd>
              <dt>Identifiant légal</dt>
              <dd>{tiers.legalIdentifier ?? <span className="aide">—</span>}</dd>
              <dt>N° de TVA</dt>
              <dd>{tiers.vatNumber ?? <span className="aide">—</span>}</dd>
              <dt>Provenance</dt>
              <dd>{tiers.sourceSystem ?? <span className="aide">saisie manuelle</span>}</dd>
            </dl>
          </div>
        </div>

        <div className="carte">
          <h2>Contact</h2>
          <div className="carte-corps">
            <dl className="proprietes">
              <dt>Courriel</dt>
              <dd>{tiers.email ?? <span className="aide">—</span>}</dd>
              <dt>Téléphone</dt>
              <dd>{tiers.phone ?? <span className="aide">—</span>}</dd>
              <dt>Ville</dt>
              <dd>
                {tiers.city ?? <span className="aide">—</span>}
                {tiers.postalCode && <span className="aide"> ({tiers.postalCode})</span>}
              </dd>
            </dl>
          </div>
        </div>
      </div>

      <ContactsTiers partyId={tiers.id} />

      <div className="carte">
        <div className="entete-carte">
          <h2>Sites ({sites.length})</h2>
          <button className="discret" onClick={() => setFormulaire('site')}>
            Ajouter un site
          </button>
        </div>
        {sites.length === 0 ? (
          <div className="vide">
            Aucun site.
            {!tiers.roles.includes('CUSTOMER') && (
              <>
                <br />
                Ce tiers n’a pas le rôle « Client » : ajoutez-le d’abord si vous voulez lui
                rattacher des sites.
              </>
            )}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Site</th>
                <th>Ville</th>
                <th style={{ width: 120 }}>Zone</th>
                <th style={{ width: 110 }}>Machines</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link className="lien-fort" to={`/sites/${s.id}`}>
                      {s.name}
                    </Link>
                    {s.addressLine1 && <div className="aide">{s.addressLine1}</div>}
                  </td>
                  <td>{s.city ?? <span className="aide">—</span>}</td>
                  <td>{s.zone ?? <span className="aide">—</span>}</td>
                  <td>{s.assetCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="carte">
        <h2>Parc ({machines.length} machines)</h2>
        {machines.length === 0 ? (
          <div className="vide">Aucune machine sur les sites de ce tiers.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Machine</th>
                <th style={{ width: 130 }}>Famille</th>
                <th>Site</th>
                <th style={{ width: 140 }}>QR</th>
                <th style={{ width: 130 }}>État</th>
              </tr>
            </thead>
            <tbody>
              {machines.map((m) => {
                const etat = STATUT_MACHINE[m.status];
                return (
                  <tr key={m.id}>
                    <td>
                      <Link className="lien-fort" to={`/machines/${m.id}`}>
                        {m.label ?? m.model ?? 'Sans désignation'}
                      </Link>
                      <div className="aide">
                        {m.brandName ?? '—'}
                        {m.model && <> · {m.model}</>}
                      </div>
                    </td>
                    <td>{m.assetType}</td>
                    <td>{m.siteName ?? <span className="aide">sans site</span>}</td>
                    <td>
                      <code className="qr">{m.qrCode}</code>
                    </td>
                    <td>
                      <span className={`etat ${etat.classe}`}>{etat.texte}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {formulaire === 'tiers' && (
        <FormulaireTiers
          tiers={tiers}
          onFermer={() => setFormulaire(null)}
          onEnregistre={() => void charger()}
        />
      )}

      {formulaire === 'site' && (
        <FormulaireSite
          site={null}
          clientImpose={tiers.id}
          onFermer={() => setFormulaire(null)}
          onEnregistre={() => void charger()}
        />
      )}
    </>
  );
}
